import React from 'react';
import { FiCalendar, FiClock, FiMessageSquare } from 'react-icons/fi';
import type { DossierCommitment, DossierCommitmentsSection, DossierOpportunityRef } from '../../../services/CrmDossierService';
import DossierSectionState from './DossierSectionState';
import DossierMoveMenu from './DossierMoveMenu';
import { COMMITMENT_STATE_LABELS, agendaPath, formatDossierWhen } from './dossierFormat';

interface Props {
  section: DossierCommitmentsSection;
  contactName: string;
  busyItemId?: string | null;
  onLink: (itemId: string) => void;
  onUnlink: (itemId: string) => void;
  /** Outras oportunidades do mesmo cliente: destino possível da correção de atribuição. */
  otherOpportunities?: DossierOpportunityRef[];
  onMove?: (itemId: string, targetCardId: number) => void;
  onOpen: (path: string) => void;
  onRetry?: () => void;
}

function stateLabel(c: DossierCommitment): string {
  if (c.byAi && c.state === 'scheduled') return 'Programado pela IA';
  return COMMITMENT_STATE_LABELS[c.state] ?? c.state;
}

function KindIcon({ kind }: { kind: DossierCommitment['kind'] }) {
  if (kind === 'meeting') return <FiCalendar aria-hidden />;
  if (kind === 'scheduled_message') return <FiClock aria-hidden />;
  return <FiMessageSquare aria-hidden />;
}

const DossierCommitments: React.FC<Props> = ({ section, contactName, busyItemId, onLink, onUnlink, otherOpportunities = [], onMove, onOpen, onRetry }) => {
  const own = section.items.filter((i) => i.scope === 'card');
  const fromContact = section.items.filter((i) => i.scope === 'contact');

  const renderItem = (c: DossierCommitment) => (
    <li key={c.id} className="dossier-item" data-testid={`dossier-commitment-${c.id}`}>
      <div className="dossier-item__icon"><KindIcon kind={c.kind} /></div>
      <div className="dossier-item__body">
        <span className={`dossier-chip dossier-chip--${c.state}`}>{stateLabel(c)}</span>
        <strong className="dossier-item__title">{c.title}</strong>
        {c.scope === 'card' && !c.linked && otherOpportunities.length > 0 && (
          <span className="dossier-chip" title="Veio da própria oportunidade principal; mova se for de outra.">atribuído automaticamente</span>
        )}
        {c.at && <span className="dossier-item__meta">{formatDossierWhen(c.at)}</span>}
        {c.quote && <span className="dossier-item__quote">{`“${c.quote}”`}</span>}
        {c.detail && <span className="dossier-item__summary">{c.detail}</span>}
        <div className="dossier-item__actions">
          {c.kind === 'meeting' && (
            <button type="button" className="dossier-btn" onClick={() => onOpen(agendaPath(c.openPath, c.at))}>
              Abrir evento
            </button>
          )}
          {c.kind === 'commitment' && (
            <button type="button" className="dossier-btn" onClick={() => onOpen(c.openPath)}>
              Abrir conversa
            </button>
          )}
          {c.kind === 'scheduled_message' && (
            <button type="button" className="dossier-btn" onClick={() => onOpen(c.openPath)}>
              Ver envios programados
            </button>
          )}
          {c.scope === 'contact' && (
            <button type="button" className="dossier-btn dossier-btn--primary" disabled={busyItemId === c.id} onClick={() => onLink(c.id)}>
              Vincular a esta oportunidade
            </button>
          )}
          {c.scope === 'card' && onMove && (
            <DossierMoveMenu itemId={c.id} others={otherOpportunities} disabled={busyItemId === c.id} onMove={onMove} />
          )}
          {c.scope === 'card' && c.linked && (
            <button type="button" className="dossier-btn" disabled={busyItemId === c.id} onClick={() => onUnlink(c.id)}>
              Desvincular
            </button>
          )}
        </div>
      </div>
    </li>
  );

  return (
    <section className="dossier-card" data-testid="dossier-commitments">
      <h3 className="dossier-card__title">Compromissos</h3>
      <DossierSectionState
        status={section.status}
        isEmpty={section.items.length === 0}
        emptyText="Nenhum compromisso nesta oportunidade."
        unavailableText="Não deu para carregar os compromissos agora."
        failedSources={section.failedSources}
        onRetry={onRetry}
      >
        {own.length > 0 && <ul className="dossier-list">{own.map(renderItem)}</ul>}
        {fromContact.length > 0 && (
          <div className="dossier-group" data-testid="dossier-commitments-contact">
            <h4 className="dossier-group__title">Do cliente, sem oportunidade definida</h4>
            <p className="dossier-note">{`Valem para todas as oportunidades de ${contactName} até você vincular a uma delas.`}</p>
            <ul className="dossier-list">{fromContact.map(renderItem)}</ul>
          </div>
        )}
      </DossierSectionState>
      {section.status !== 'unavailable' && section.otherOpportunityCount > 0 && (
        <p className="dossier-note">{`${section.otherOpportunityCount} compromisso(s) em outras oportunidades deste cliente.`}</p>
      )}
      {section.status !== 'unavailable' && section.truncated && <p className="dossier-note">Mostrando os mais recentes.</p>}
    </section>
  );
};

export default DossierCommitments;
